import React from 'react';
import { nextI18nLocales } from '@peloton/internationalize/models/nextI18n';
import { sanitizeReturnTo } from '@auth/utils/sanitizeReturnTo';
import type { LoginRedirectOpts } from '@auth/utils/types';
import { useAuth } from './AuthProvider';

export const DEFAULT_BYPASS_AUTH_PATHS: readonly string[] = [
  '/callback',
  '/signup/callback',
];

export const stripLocalePrefix = (pathname: string): string => {
  const localePrefix = nextI18nLocales.find(
    locale => pathname === `/${locale}` || pathname.startsWith(`/${locale}/`),
  );
  if (!localePrefix) {
    return pathname;
  }
  return pathname.slice(`/${localePrefix}`.length) || '/';
};

export type UseRequiresAuthOptions = {
  requiresAuth?: boolean;

  /**
   * App-specific paths where the auth guard should always render its children
   * and never trigger a login redirect, on top of `DEFAULT_BYPASS_AUTH_PATHS`
   * (which is always included and does not need to be repeated here).
   * Useful for any one-off path that needs to not be gated behind auth.
   */
  bypassAuthPaths?: readonly string[];

  /**
   * @per-app
   *
   * Called when the user is unauthenticated and the guard is about to call
   * `loginWithRedirect`. Return a URL string to redirect there *instead* of
   * Auth0 (e.g. Members' prospects guest-pass external redirect, or an app
   * that wants to send guests to a marketing site rather than the login screen).
   *
   * Return `null`, `undefined`, or empty string to fall through to the default
   * Auth0 login flow.
   */
  loginRedirectOverride?: () => string | null | undefined;

  /**
   * @per-app
   *
   * Build the `LoginRedirectOpts` passed to `loginWithRedirect` when falling
   * through to the default flow. Lets each app inject its own:
   *  - `fragment`              (Auth0 universal-login screen selector)
   *  - `authorizationParams`   (UTM params, `redirect_uri`, ...)
   *  - `redirectMethod`
   *
   * Receives the captured post-login URL so consumers can derive params from it.
   * If omitted, no extra options are forwarded — Auth0's defaults apply.
   */
  loginOptions?: (postLoginUrl: string) => LoginRedirectOpts | undefined;

  /**
   * @per-app
   *
   * Override the URL captured for post-login redirect. The default is
   * `window.location.pathname + window.location.search` (relative path)
   */
  postLoginUrl?: () => string;
};

export type UseRequiresAuthResult = {
  isAuthenticated: boolean;
  isLoading: boolean;
  shouldRender: boolean;
};

export const defaultPostLoginUrl = (): string => {
  if (typeof window === 'undefined') {
    return '/';
  }
  return window.location.pathname + window.location.search;
};

export const useRequiresAuth = (
  options: UseRequiresAuthOptions = {},
): UseRequiresAuthResult => {
  const {
    requiresAuth = true,
    bypassAuthPaths: extraBypassAuthPaths = [],
    loginRedirectOverride,
    loginOptions,
    postLoginUrl,
  } = options;
  const bypassAuthPaths = [...DEFAULT_BYPASS_AUTH_PATHS, ...extraBypassAuthPaths];

  const isBypassAuthPath = (() => {
    if (typeof window === 'undefined') {
      return false;
    }
    const pathname = stripLocalePrefix(window.location.pathname);
    return bypassAuthPaths.some(
      path => pathname === path || pathname.startsWith(`${path}/`),
    );
  })();

  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth();

  const loginRedirectOverrideRef = React.useRef(loginRedirectOverride);
  const loginOptionsRef = React.useRef(loginOptions);
  const postLoginUrlRef = React.useRef(postLoginUrl);
  loginRedirectOverrideRef.current = loginRedirectOverride;
  loginOptionsRef.current = loginOptions;
  postLoginUrlRef.current = postLoginUrl;

  const navigationDispatched = React.useRef(false);

  React.useEffect(() => {
    if (isBypassAuthPath) {
      return;
    }

    if (isLoading) {
      return;
    }

    if (!requiresAuth) {
      return;
    }

    if (isAuthenticated) {
      navigationDispatched.current = false;
      return;
    }

    if (navigationDispatched.current) {
      return;
    }
    navigationDispatched.current = true;

    const rawOverride = loginRedirectOverrideRef.current?.();
    if (rawOverride) {
      const safeOverride = sanitizeReturnTo(rawOverride, '');
      if (safeOverride) {
        if (typeof window !== 'undefined') {
          window.location.href = safeOverride;
        }
        return;
      }
    }

    const rawPostLoginUrl = (postLoginUrlRef.current ?? defaultPostLoginUrl)();
    const safePostLoginUrl = sanitizeReturnTo(rawPostLoginUrl, '/');
    const opts = loginOptionsRef.current?.(safePostLoginUrl);
    loginWithRedirect(safePostLoginUrl, opts).catch(() => {
      navigationDispatched.current = false;
    });
  }, [requiresAuth, isLoading, isAuthenticated, loginWithRedirect, isBypassAuthPath]);

  const shouldRender = (() => {
    if (isBypassAuthPath) {
      return true;
    }
    if (!requiresAuth) {
      return true;
    }
    if (isLoading) {
      return false;
    }
    return isAuthenticated;
  })();

  return { isAuthenticated, isLoading, shouldRender };
};
